/**
 * useAdminAuth — drives the admin login flow inside the TMA.
 *
 * State machine:
 *   'unauth'        — no session token yet, magic link not requested
 *   'magic-pending' — POST /v1/admin/auth/request-link succeeded; user is
 *                     waiting on the Telegram DM
 *   'authed'        — a valid session token is in memory + localStorage and
 *                     the /v1/admin/overview probe returned 200
 *   'not-allowed'   — server returned 403 (telegram_id not in allowlist)
 *   'error'         — network failure or unexpected status; UI shows retry
 *
 * On mount:
 *   1. If `?token=` is present in window.location (the magic-link landing),
 *      POST /exchange and stash the resulting session token in localStorage,
 *      then strip the query param so refresh doesn't replay.
 *   2. Otherwise, read cached session token from localStorage and probe
 *      /overview to verify it's still valid. On 401 → drop cache, become
 *      'unauth'. On 403 → 'not-allowed'.
 *
 * The Telegram-native landing path (deep link) drops the magic token into
 * `window.Telegram.WebApp.initDataUnsafe.start_param` formatted as
 * `admin-<token>`. We accept both `?token=` (web preview) and the start_param.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'sagwa.admin.session'

export type AdminAuthStatus =
  | 'unauth'
  | 'magic-pending'
  | 'authed'
  | 'not-allowed'
  | 'error'

export type UseAdminAuthResult = {
  status: AdminAuthStatus
  sessionToken: string | null
  errorMessage: string | null
  requestMagic: () => Promise<void>
  exchangeToken: (token: string) => Promise<void>
  signOut: () => void
}

type CachedSession = {
  sessionToken: string
  expiresAtMs: number
}

const BASE_URL: string =
  (typeof import.meta !== 'undefined' &&
    (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_API_BASE_URL) ||
  ''

function getInitData(): string {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined
  return tg?.initData ?? ''
}

function getStartParam(): string | null {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined
  const sp = tg?.initDataUnsafe?.start_param
  return typeof sp === 'string' && sp ? sp : null
}

/** Extract the magic token from `?token=`, `?startapp=admin-<token>`, or start_param. */
function extractMagicToken(): string | null {
  if (typeof window === 'undefined') return null
  // 1) Direct ?token=...
  const params = new URLSearchParams(window.location.search)
  const direct = params.get('token')
  if (direct) return direct
  // 2) ?startapp=admin-<token>
  const startApp = params.get('startapp') ?? params.get('tgWebAppStartParam')
  if (startApp && startApp.startsWith('admin-')) {
    return startApp.slice('admin-'.length)
  }
  // 3) Telegram start_param
  const sp = getStartParam()
  if (sp && sp.startsWith('admin-')) {
    return sp.slice('admin-'.length)
  }
  return null
}

function readCachedSession(): CachedSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedSession
    if (
      typeof parsed?.sessionToken !== 'string' ||
      typeof parsed?.expiresAtMs !== 'number' ||
      parsed.expiresAtMs <= Date.now()
    ) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeCachedSession(session: CachedSession): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Quota/privacy mode: silently fall through; in-memory state still works.
  }
}

function clearCachedSession(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* no-op */
  }
}

/** Strip `token` / `startapp` from the URL without forcing a reload. */
function cleanUrlSearch(): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  let changed = false
  for (const k of ['token', 'startapp', 'tgWebAppStartParam']) {
    if (url.searchParams.has(k)) {
      url.searchParams.delete(k)
      changed = true
    }
  }
  if (changed) {
    window.history.replaceState({}, '', url.toString())
  }
}

export function useAdminAuth(): UseAdminAuthResult {
  const [status, setStatus] = useState<AdminAuthStatus>('unauth')
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const initialized = useRef(false)

  const probeSession = useCallback(async (token: string): Promise<boolean> => {
    try {
      const res = await fetch(`${BASE_URL}/v1/admin/overview`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `tma-admin ${token}`,
        },
      })
      if (res.status === 200) return true
      if (res.status === 401) {
        clearCachedSession()
        return false
      }
      if (res.status === 403) {
        clearCachedSession()
        setStatus('not-allowed')
        return false
      }
      return false
    } catch {
      return false
    }
  }, [])

  const exchangeToken = useCallback(async (magicToken: string) => {
    setErrorMessage(null)
    try {
      const res = await fetch(`${BASE_URL}/v1/admin/auth/exchange`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: magicToken }),
      })
      if (res.status === 403) {
        setStatus('not-allowed')
        return
      }
      if (!res.ok) {
        setStatus('error')
        setErrorMessage(`Exchange failed (${res.status})`)
        return
      }
      const body = (await res.json()) as {
        ok: true
        sessionToken: string
        expiresAtMs: number
      }
      if (!body?.ok || !body.sessionToken) {
        setStatus('error')
        setErrorMessage('Invalid exchange response')
        return
      }
      writeCachedSession({
        sessionToken: body.sessionToken,
        expiresAtMs: body.expiresAtMs,
      })
      setSessionToken(body.sessionToken)
      setStatus('authed')
    } catch (err) {
      setStatus('error')
      setErrorMessage(err instanceof Error ? err.message : 'Exchange failed')
    }
  }, [])

  const requestMagic = useCallback(async () => {
    setErrorMessage(null)
    const initData = getInitData()
    if (!initData) {
      setStatus('error')
      setErrorMessage('Telegram initData unavailable')
      return
    }
    try {
      const res = await fetch(`${BASE_URL}/v1/admin/auth/request-link`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `tma ${initData}`,
        },
      })
      if (res.status === 403) {
        setStatus('not-allowed')
        return
      }
      if (!res.ok) {
        setStatus('error')
        setErrorMessage(`Magic-link request failed (${res.status})`)
        return
      }
      setStatus('magic-pending')
    } catch (err) {
      setStatus('error')
      setErrorMessage(err instanceof Error ? err.message : 'Network error')
    }
  }, [])

  const signOut = useCallback(() => {
    clearCachedSession()
    setSessionToken(null)
    setStatus('unauth')
    setErrorMessage(null)
  }, [])

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    const magicToken = extractMagicToken()
    if (magicToken) {
      cleanUrlSearch()
      void exchangeToken(magicToken)
      return
    }

    const cached = readCachedSession()
    if (!cached) {
      setStatus('unauth')
      return
    }
    setSessionToken(cached.sessionToken)
    void probeSession(cached.sessionToken).then((ok) => {
      if (ok) {
        setStatus('authed')
      } else {
        setSessionToken(null)
        // probeSession already set 'not-allowed' on 403; only override when
        // still unauth (i.e. 401 or network drop).
        setStatus((s) => (s === 'not-allowed' ? s : 'unauth'))
      }
    })
  }, [exchangeToken, probeSession])

  return {
    status,
    sessionToken,
    errorMessage,
    requestMagic,
    exchangeToken,
    signOut,
  }
}
